import { useState, useEffect } from 'react';
import { IconRefresh, IconPlus, IconEdit, IconTrash, IconLoader2, IconX } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

/**
 * DEV-1950 (FR004/FR005) — operator-managed test recipients, replacing the
 * env-config lists (`CAMPAIGN_TEST_APP_USER_UUIDS`/`_ACCOUNT_UUIDS`/
 * `_EMAILS`) and `sendTestSms`'s hard-coded array. This page is CRUD only —
 * per-campaign include/exclude selection at send time lives on
 * CampaignsPage's Send-Test picker.
 */

interface TestRecipient {
  id: string;
  label: string;
  msisdn: string | null;
  email: string | null;
  appUserUuid: string | null;
  accountUuid: string | null;
  firstName: string | null;
  lastName: string | null;
  mvnoId: string | null;
  enabled: boolean;
  createdBy: string;
  /** Operator who last edited this entry (B7) — null until the first edit.
   * A row that controls where real test content is delivered needs an
   * attribution trail for edits, not just creation. */
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CampaignMvno {
  id: string;
  label: string;
}

/** Server-side create/update payload. Empty-string fields are sent as `null`
 * (clear) rather than omitted — the form always sets every field it shows,
 * there is no partial-omission use case in this UI (unlike the raw API,
 * which supports "leave unchanged" for a caller with no form state to
 * re-submit). */
interface RecipientFormState {
  label: string;
  msisdn: string;
  email: string;
  appUserUuid: string;
  accountUuid: string;
  firstName: string;
  lastName: string;
  mvnoId: string;
  enabled: boolean;
}

const EMPTY_FORM: RecipientFormState = {
  label: '',
  msisdn: '',
  email: '',
  appUserUuid: '',
  accountUuid: '',
  firstName: '',
  lastName: '',
  mvnoId: 'megsapp',
  enabled: true,
};

const getApiErrorMessage = (err: any, fallback: string): string => (
  err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback
);

function toFormState(recipient: TestRecipient): RecipientFormState {
  return {
    label: recipient.label,
    msisdn: recipient.msisdn || '',
    email: recipient.email || '',
    appUserUuid: recipient.appUserUuid || '',
    accountUuid: recipient.accountUuid || '',
    firstName: recipient.firstName || '',
    lastName: recipient.lastName || '',
    mvnoId: recipient.mvnoId || 'megsapp',
    enabled: recipient.enabled,
  };
}

/** `''` submits as `null` (clear/omit) for every optional field — only
 * `label` and `enabled` are always required by the API. */
function toPayload(form: RecipientFormState): Record<string, unknown> {
  return {
    label: form.label.trim(),
    msisdn: form.msisdn.trim() || null,
    email: form.email.trim() || null,
    appUserUuid: form.appUserUuid.trim() || null,
    accountUuid: form.accountUuid.trim() || null,
    firstName: form.firstName.trim() || null,
    lastName: form.lastName.trim() || null,
    mvnoId: form.mvnoId.trim() || null,
    enabled: form.enabled,
  };
}

function IdentifierBadges({ recipient }: { recipient: TestRecipient }) {
  return (
    <div className="flex flex-wrap gap-1">
      {recipient.msisdn && <Badge variant="outline">SMS: {recipient.msisdn}</Badge>}
      {recipient.email && <Badge variant="outline">Email: {recipient.email}</Badge>}
      {recipient.appUserUuid && <Badge variant="outline">Inbox/Push</Badge>}
      {/* The mvno badge is the whole point of the explicitness requirement
          (DEV-1950): a uuid-bearing row must never look brand-neutral — it
          was verified against exactly this Nova CDC dataset, never a
          silent default the operator cannot see. */}
      {recipient.mvnoId && <Badge variant="secondary">{recipient.mvnoId}</Badge>}
    </div>
  );
}

/** Add/edit form. Renders inside a Dialog; `null` target means "create". */
function RecipientFormDialog({
  open,
  target,
  onClose,
  onSaved,
}: {
  open: boolean;
  target: TestRecipient | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<RecipientFormState>(EMPTY_FORM);
  const [availableMvnos, setAvailableMvnos] = useState<CampaignMvno[]>([]);
  const [loadingMvnos, setLoadingMvnos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(target ? toFormState(target) : EMPTY_FORM);
      setFormError('');
      setLoadingMvnos(true);
      api.get('/campaigns/available-mvnos')
        .then((response) => setAvailableMvnos(response.data.mvnos || []))
        .catch((err) => {
          setAvailableMvnos([]);
          setFormError(getApiErrorMessage(err, 'Failed to load available brands'));
        })
        .finally(() => setLoadingMvnos(false));
    }
  }, [open, target]);

  const handleSave = async () => {
    if (!form.label.trim()) { setFormError('A label is required'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (target) {
        await api.put(`/campaigns/test-recipients/${encodeURIComponent(target.id)}`, toPayload(form));
      } else {
        await api.post('/campaigns/test-recipients', toPayload(form));
      }
      onSaved();
    } catch (err: any) {
      setFormError(getApiErrorMessage(err, 'Failed to save test recipient'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{target ? 'Edit Test Recipient' : 'Add Test Recipient'}</DialogTitle>
          <DialogDescription>
            A label plus at least one identifier (MSISDN, email, or app/device id). App/device id
            requires a paired account id, and both are verified against the named (or default
            megsApp) Nova CDC dataset before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="tr-label">Label</Label>
            <Input
              id="tr-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Jed - QA"
              maxLength={120}
              disabled={saving}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tr-msisdn">MSISDN (SMS)</Label>
              <Input
                id="tr-msisdn"
                value={form.msisdn}
                onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
                placeholder="0821234567"
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="tr-email">Email</Label>
              <Input
                id="tr-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jed@85.group"
                disabled={saving}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tr-app-user-uuid">App/Device ID (Inbox/Push)</Label>
              <Input
                id="tr-app-user-uuid"
                value={form.appUserUuid}
                onChange={(e) => setForm({ ...form, appUserUuid: e.target.value })}
                placeholder="uuid"
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="tr-account-uuid">Account ID (Inbox — pairs with App/Device ID)</Label>
              <Input
                id="tr-account-uuid"
                value={form.accountUuid}
                onChange={(e) => setForm({ ...form, accountUuid: e.target.value })}
                placeholder="uuid"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="tr-mvno">Brand (verifies App/Device ID + Account ID)</Label>
            <Select
              value={form.mvnoId}
              onValueChange={(value) => setForm({ ...form, mvnoId: value || 'megsapp' })}
              disabled={saving || loadingMvnos}
            >
              <SelectTrigger id="tr-mvno" className="w-full">
                <SelectValue placeholder={loadingMvnos ? 'Loading brands...' : 'Select brand'} />
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)]">
                {availableMvnos.map((mvno) => (
                  <SelectItem key={mvno.id} value={mvno.id}>
                    {mvno.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tr-first-name">First Name (demo, optional)</Label>
              <Input
                id="tr-first-name"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                placeholder="Jed"
                maxLength={64}
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="tr-last-name">Last Name (demo, optional)</Label>
              <Input
                id="tr-last-name"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                placeholder="Naidoo"
                maxLength={64}
                disabled={saving}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Optional demo values so {'{{first_name}}'}/{'{{surname}}'} render on a test send instead
            of tripping the missing-placeholder-data block.
          </p>

          <div className="flex items-center gap-2">
            <Switch
              checked={form.enabled}
              onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              disabled={saving}
            />
            <Label>Enabled (eligible for the default test-send selection)</Label>
          </div>

          {formError && (
            <Alert variant="destructive">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <IconLoader2 size={16} className="animate-spin" /> : null}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRecipientDialog({
  target,
  deleting,
  deleteError,
  onConfirm,
  onClose,
}: {
  target: TestRecipient | null;
  deleting: boolean;
  deleteError: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open && !deleting) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove Test Recipient</DialogTitle>
          <DialogDescription>
            Removing "{target?.label}" does not affect campaigns already sent to it — a test send
            copies identifiers at send time rather than storing a reference back to this row.
          </DialogDescription>
        </DialogHeader>
        {deleteError && (
          <Alert variant="destructive">
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={deleting}>
            {deleting ? <IconLoader2 size={16} className="animate-spin" /> : <IconTrash size={16} />}
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TestRecipientsPage() {
  const [recipients, setRecipients] = useState<TestRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<TestRecipient | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TestRecipient | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadRecipients = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/campaigns/test-recipients');
      setRecipients(response.data.recipients || []);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load test recipients'));
      setRecipients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRecipients(); }, []);

  const openCreate = () => { setFormTarget(null); setFormOpen(true); };
  const openEdit = (recipient: TestRecipient) => { setFormTarget(recipient); setFormOpen(true); };

  const handleSaved = async () => {
    setFormOpen(false);
    setSuccess(formTarget ? 'Test recipient updated' : 'Test recipient created');
    setError('');
    await loadRecipients();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete(`/campaigns/test-recipients/${encodeURIComponent(deleteTarget.id)}`);
      setSuccess(`Removed "${deleteTarget.label}"`);
      setDeleteTarget(null);
      await loadRecipients();
    } catch (err: any) {
      setDeleteError(getApiErrorMessage(err, 'Failed to remove test recipient'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Test Recipients"
        subtitle="Manage the operator-controlled list of test recipients used by campaign test sends."
      />

      <div className="flex items-center gap-4 mb-4">
        <Button onClick={loadRecipients} disabled={loading} variant="outline">
          <IconRefresh className="mr-2 h-4 w-4" />
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        <Button onClick={openCreate}>
          <IconPlus className="mr-2 h-4 w-4" />
          Add Recipient
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="icon-xs" onClick={() => setError('')}><IconX size={14} /></Button>
          </AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {success}
            <Button variant="ghost" size="icon-xs" onClick={() => setSuccess('')}><IconX size={14} /></Button>
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex justify-center p-8">
          <IconLoader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {!loading && recipients.length > 0 && (
        <MainCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">Label</TableHead>
                <TableHead className="font-bold">Identifiers</TableHead>
                <TableHead className="font-bold">Demo Name</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="font-bold">Created By</TableHead>
                <TableHead className="font-bold">Created</TableHead>
                <TableHead className="font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.map((recipient) => (
                <TableRow key={recipient.id}>
                  <TableCell className="font-medium">{recipient.label}</TableCell>
                  <TableCell><IdentifierBadges recipient={recipient} /></TableCell>
                  <TableCell>
                    {[recipient.firstName, recipient.lastName].filter(Boolean).join(' ') || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {recipient.enabled ? (
                      <Badge variant="success">Enabled</Badge>
                    ) : (
                      <Badge variant="secondary">Disabled</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{recipient.createdBy}</div>
                    {recipient.updatedBy && (
                      <div className="text-muted-foreground">edited by {recipient.updatedBy}</div>
                    )}
                  </TableCell>
                  <TableCell>{new Date(recipient.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(recipient)}>
                        <IconEdit size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive"
                        onClick={() => { setDeleteTarget(recipient); setDeleteError(''); }}
                      >
                        <IconTrash size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MainCard>
      )}

      {!loading && recipients.length === 0 && !error && (
        <MainCard className="text-center">
          <p className="text-muted-foreground">
            No test recipients configured yet. Add one, or campaign test sends fall back to the
            legacy hard-coded/env-configured set.
          </p>
        </MainCard>
      )}

      <RecipientFormDialog
        open={formOpen}
        target={formTarget}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
      <DeleteRecipientDialog
        target={deleteTarget}
        deleting={deleting}
        deleteError={deleteError}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
